import { createSelector } from '@ngrx/store';
import { each, find } from 'lodash';

import { Permission } from '../_models/permission.model';
import { Role } from '../_models/role.model';
import { TypeSystem } from '../../../support/type-system.support';
import { selectAllPermissions } from './permission.selectors';
import { selectAllRoles } from './role.selectors';

export const selectAuthState = state => state.auth;

export const isLoggedIn = createSelector(
    selectAuthState,
    auth => auth.loggedIn
);

export const isLoggedOut = createSelector(
    isLoggedIn,
    loggedIn => !loggedIn
);


export const currentAuthToken = createSelector(
    selectAuthState,
    auth => auth.authToken
);

export const isUserLoaded = createSelector(
    selectAuthState,
    auth => auth.isUserLoaded
);

export const currentUser = createSelector(
    selectAuthState,
    auth => auth.user
);

export const currentUserRoleIds = createSelector(
    currentUser,
    user => !user ? [] : user.roles
);

export const currentUserPermissionsIds = createSelector(
    currentUserRoleIds,
    selectAllRoles,
    (userRoleIds: string[], allRoles: Role[]) => getPermissionsIdsFrom(userRoleIds, allRoles)
);

export const checkHasUserPermission = (permissionId: string) => createSelector(
    currentUserPermissionsIds,
    (ids: string[]) => ids.some(id => id === permissionId)
);

export const currentUserPermissions = createSelector(
    currentUserPermissionsIds,
    selectAllPermissions,
    (permissionIds: string[], allPermissions: Permission[]) => {
        const result: Permission[] = [];
        each(permissionIds, id => {
            const userPermission = find(allPermissions, elem => elem.id === id);
            if (userPermission) {
                result.push(userPermission);
            }
        });
        return result;
    }
);

/**
 * @description
 *  Computa la lista de permisos asociados a los roles que posee un usuario.
 *
 * @param userRolesIds
 *  Los roles de este usuario.
 * @param allRoles
 *  La lista de todos los roles que maneja la aplicación.
 *
 * @returns
 *  Un vector de permisos que el usuario posee debido a los roles que tiene.
 */
function getPermissionsIdsFrom(userRolesIds: string[] = [], allRoles: Role[] = []): string[] {
    const flattenized: string[] = TypeSystem.flattenize(userRolesIds);
    const roles: Set<string> = new Set<string>(flattenized);
    return allRoles
        .filter((role: Role) => roles.has(role.id))
        .map((role: Role) => role.permissions)
        .reduce((accumulator: string[], permissions: string[]) => accumulator.concat(permissions), []);
}
